// Responsive Types
export type ResponsiveObj<T> = {
  mob?: T;
  tab?: T;
  web?: T;
};
export type ResponsiveProp<T> = T | T[] | ResponsiveObj<T>;

// Base Props
export interface UiBaseProps {
  size?: UiSize | ResponsiveProp<UiSize>;
  fontSize?:
    | UiFontSize
    | string
    | number
    | ResponsiveProp<UiFontSize | string | number>;
  variant?: UiVariant;
  colorPalette?: UiColor | string;
  bgcolor?:
    | UiBackgroundColor
    | string
    | ResponsiveProp<UiBackgroundColor | string>;
  rounded?:
    | UiRounded
    | string
    | number
    | ResponsiveProp<UiRounded | string | number>;
  shadow?: UiShadow | string | ResponsiveProp<UiShadow | string>;
  padding?: UiSpacing | string | ResponsiveProp<UiSpacing | string>;
  margin?: UiSpacing | string | ResponsiveProp<UiSpacing | string>;
  borderWidth?: UiBorderWidth | string | number;
  lineHeight?: UiLineHeight | string | number;
  letterSpacing?: UiLetterSpacing | string | number;
  transition?: UiTransition | string;
  height?: string | number;
  width?: string | number;
}

// Sizes
export const UiSize = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  "2xl": "2xl",
} as const;
export type UiSize = (typeof UiSize)[keyof typeof UiSize];

// Font Sizes
export const UiFontSize = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  "2xl": "2xl",
  "3xl": "3xl",
} as const;
export type UiFontSize = (typeof UiFontSize)[keyof typeof UiFontSize];

// Variants
export const UiVariant = {
  solid: "solid",
  subtle: "subtle",
  surface: "surface",
  outline: "outline",
  ghost: "ghost",
  plain: "plain",
} as const;
export type UiVariant = (typeof UiVariant)[keyof typeof UiVariant];

// Colors
export const UiColor = {
  none: "none",
  gray: "gray",
  red: "red",
  orange: "orange",
  yellow: "yellow",
  green: "green",
  teal: "teal",
  blue: "blue",
  cyan: "cyan",
  purple: "purple",
  pink: "pink",
  black: "black",
  white: "white",
  transparent: "transparent",
  inherit: "inherit",
  current: "current",
} as const;
export type UiColor = (typeof UiColor)[keyof typeof UiColor];

// Rounded
export const UiRounded = {
  none: "none",
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  "2xl": "2xl",
  full: "full",
  l1: "l1",
  l2: "l2",
  l3: "l3",
} as const;
export type UiRounded = (typeof UiRounded)[keyof typeof UiRounded];

// Shadows
export const UiShadow = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  none: "none",
} as const;
export type UiShadow = (typeof UiShadow)[keyof typeof UiShadow];

// Shadow Colors
export const UiShadowColor = {
  black: "rgba(0, 0, 0, 0.25)",
  gray: "rgba(107, 114, 128, 0.25)",
  blue: "rgba(59, 130, 246, 0.25)",
  red: "rgba(239, 68, 68, 0.25)",
  green: "rgba(34, 197, 94, 0.25)",
  none: "transparent",
} as const;
export type UiShadowColor =
  | (typeof UiShadowColor)[keyof typeof UiShadowColor]
  | string;

// Spacing
export const UiSpacing = {
  none: "0",
  xs: "1",
  sm: "2",
  md: "4",
  lg: "6",
  xl: "8",
  "10": "10",
  "12": "12",
  "16": "16",
  "20": "20",
} as const;
export type UiSpacing =
  | (typeof UiSpacing)[keyof typeof UiSpacing]
  | string
  | number;

// Border Width
export const UiBorderWidth = {
  none: "0",
  thin: "1px",
  thick: "2px",
  heavy: "4px",
} as const;
export type UiBorderWidth =
  | (typeof UiBorderWidth)[keyof typeof UiBorderWidth]
  | string
  | number;

// Line Height
export const UiLineHeight = {
  short: "short",
  base: "base",
  tall: "tall",
} as const;
export type UiLineHeight =
  | (typeof UiLineHeight)[keyof typeof UiLineHeight]
  | string
  | number;

// Letter Spacing
export const UiLetterSpacing = {
  tighter: "tighter",
  tight: "tight",
  normal: "normal",
  wide: "wide",
  wider: "wider",
  widest: "widest",
} as const;
export type UiLetterSpacing =
  | (typeof UiLetterSpacing)[keyof typeof UiLetterSpacing]
  | string
  | number;

// Transition
export const UiTransition = {
  fast: "all 0.2s ease-in-out",
  normal: "all 0.3s ease-in-out",
  slow: "all 0.5s ease-in-out",
} as const;
export type UiTransition =
  | (typeof UiTransition)[keyof typeof UiTransition]
  | string;

// Background Colors
export const UiBackgroundColor = {
  transparent: "transparent",
  gray: "gray",
  red: "red",
  orange: "orange",
  yellow: "yellow",
  green: "green",
  teal: "teal",
  blue: "blue",
  cyan: "cyan",
  purple: "purple",
  pink: "pink",
  black: "black",
  white: "white",
  inherit: "inherit",
  current: "current",
  colorPalette: "colorPalette",
  border: "border",
  bg: "bg",

  // Extra shades
  "whiteAlpha.50": "whiteAlpha.50",
  "whiteAlpha.100": "whiteAlpha.100",
  "whiteAlpha.200": "whiteAlpha.200",
  "whiteAlpha.300": "whiteAlpha.300",
  "whiteAlpha.400": "whiteAlpha.400",
  "whiteAlpha.500": "whiteAlpha.500",
  "whiteAlpha.600": "whiteAlpha.600",
  "whiteAlpha.700": "whiteAlpha.700",
  "whiteAlpha.800": "whiteAlpha.800",
  "whiteAlpha.900": "whiteAlpha.900",
  "whiteAlpha.950": "whiteAlpha.950",

  "blackAlpha.50": "blackAlpha.50",
  "blackAlpha.100": "blackAlpha.100",
  "blackAlpha.200": "blackAlpha.200",
  "blackAlpha.300": "blackAlpha.300",
  "blackAlpha.400": "blackAlpha.400",
  "blackAlpha.500": "blackAlpha.500",
  "blackAlpha.600": "blackAlpha.600",
  "blackAlpha.700": "blackAlpha.700",
  "blackAlpha.800": "blackAlpha.800",
  "blackAlpha.900": "blackAlpha.900",
  "blackAlpha.950": "blackAlpha.950",
} as const;
export type UiBackgroundColor =
  (typeof UiBackgroundColor)[keyof typeof UiBackgroundColor];

//------------------------------------------------------
// export interface UiBaseProps {
//     size?: UiSize | ResponsiveProp<UiSize>;
//     fontSize?: UiFontSize | ResponsiveProp<UiFontSize>;
//     variant?: UiVariant;
//     colorPalette?: UiColor;
//     rounded?: UiRounded | ResponsiveProp<UiRounded>;
//     shadow?: UiShadow | ResponsiveProp<UiShadow>;
//     shadowColor?: UiShadowColor | ResponsiveProp<UiShadowColor>;
//     padding?: UiSpacing | string | number | ResponsiveProp<UiSpacing | string | number>;
//     margin?: UiSpacing | string | number | ResponsiveProp<UiSpacing | string | number>;
//     borderWidth?: UiBorderWidth | string | number;
//     lineHeight?: UiLineHeight | string | number;
//     letterSpacing?: UiLetterSpacing | string | number;
//     transition?: UiTransition | string;
// }

// // Helper type for responsive
// export type ResponsiveProp<T> = T | { mobile?: T; tab?: T; web?: T; xl?: T };
